/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-15
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';

import Header from '../../layout/Header';
import { StudioHeader } from '../../navigation/studio';
import { useGetStudioQuery } from '../../../features/studios/studiosApi';
import { useStudioPermissions } from '../../../hooks/useStudioPermissions';
import JoinRequestList from '../../studios/joinRequests/JoinRequestList';
import LiveRegion from '../../common/LiveRegion';
import { ARIA_ROLES } from '../../../utils/accessibility';

/**
 * Join Requests page component for studio management.
 * Displays and manages join requests for a specific studio.
 */
const JoinRequestsPage: React.FC = () => {
  const { studioId } = useParams<{ studioId: string }>();

  // Fetch studio data
  const {
    data: studio,
    isLoading,
    error,
  } = useGetStudioQuery(studioId!, {
    skip: !studioId,
  });

  // Get user permissions for this studio
  const { permissions, role } = useStudioPermissions();

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <StudioHeader loading />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <LiveRegion
            message="Loading join requests, please wait..."
            politeness="POLITE"
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '50vh',
              gap: 2,
            }}
          >
            <CircularProgress size={50} />
            <Typography variant="h6" color="text.secondary">
              Loading join requests...
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  // Show error state
  if (error || !studio) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <StudioHeader studio={null} />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <LiveRegion
            message="Error loading studio information. Please try refreshing the page."
            politeness="ASSERTIVE"
          />
          <Alert severity="error" sx={{ mb: 4 }} role="alert">
            {error && 'status' in error && error.status === 404
              ? 'Studio not found. Please check the URL and try again.'
              : 'Failed to load studio information. Please try refreshing the page.'}
          </Alert>
        </Container>
      </Box>
    );
  }

  // Check permissions
  if (!permissions?.canInviteMembers) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <StudioHeader
          studio={studio}
          userRole={role}
          permissions={permissions}
        />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="warning" role="alert">
            <Typography variant="h6" gutterBottom>
              Access Denied
            </Typography>
            <Typography variant="body2">
              You don&apos;t have permission to manage join requests for this
              studio.
            </Typography>
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      <Header />
      <StudioHeader studio={studio} userRole={role} permissions={permissions} />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <LiveRegion
          message={`Join requests page loaded for ${studio.name}`}
          politeness="POLITE"
        />

        <Box
          role={ARIA_ROLES.MAIN}
          aria-label="Join requests management"
          sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
        >
          {/* Page Header */}
          <Box>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Join Requests
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage requests from users who want to join {studio.name}.
            </Typography>
          </Box>

          {/* Join Request List */}
          <JoinRequestList studioId={studio.id} />
        </Box>
      </Container>
    </Box>
  );
};

export default JoinRequestsPage;
